/*
 * lib/sync-queue.js
 *
 * Local-first pijler 2: persisteert pending writes in localStorage zodat
 * mutaties die offline gebeuren (of falen door netwerk) niet verloren
 * gaan. Wordt op `window.online` automatisch geflush, plus opnieuw bij
 * elke nieuwe enqueue.
 *
 * Hoe in te haken:
 *   import { sendOrQueue, pendingCount, onChange, flush } from './sync-queue.js';
 *   sendOrQueue('upsertExpense', row);
 *
 * sendOrQueue:
 *   - online + wrapper slaagt   → write gaat door, niets in queue
 *   - online + netwerkfout      → wordt geënqueued + flushed retry-loop
 *   - offline (navigator.onLine === false) → meteen geënqueued
 *
 * Conflict-resolutie: bij 404 / PGRST116 (rij is intussen verwijderd door
 * iemand anders) droppen we de op stilletjes uit de queue — pijler 3.
 */

import {
  upsertExpense, deleteExpense,
  upsertIncome, deleteIncome,
  upsertWeeklyBudget, deleteWeeklyBudget,
  upsertCategory, deleteCategory,
  upsertSavingsGoal, deleteSavingsGoal,
  upsertExtraEntry, deleteExtraEntry,
  patchProfile,
  updateHousehold
} from './supabase.js';

const KEY = 'florijn:queue:v1';

// Whitelist van toegestane operaties. Alleen deze namen worden uit de
// queue gehonoreerd — onbekende ops (uit een toekomstige versie ofzo)
// worden gedropt zodat we niet vastlopen op rotte data.
const HANDLERS = {
  upsertExpense, deleteExpense,
  upsertIncome, deleteIncome,
  upsertWeeklyBudget, deleteWeeklyBudget,
  upsertCategory, deleteCategory,
  upsertSavingsGoal, deleteSavingsGoal,
  upsertExtraEntry, deleteExtraEntry,
  patchProfile,
  updateHousehold
};

let queue = loadQueue();
let flushing = false;
const listeners = new Set();

function loadQueue() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveQueue() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(queue));
  } catch (err) {
    console.warn('Queue save failed (quota?):', err);
  }
}

function notify() {
  listeners.forEach(cb => { try { cb(queue.length); } catch {} });
}

/** Subscribe op queue-length changes. Returns unsubscribe-functie. */
export function onChange(cb) {
  listeners.add(cb);
  cb(queue.length);    // direct meegeven van de huidige lengte
  return () => listeners.delete(cb);
}

export function pendingCount() {
  return queue.length;
}

/** Voeg een operatie toe en probeer direct te flushen. */
export function enqueue(type, ...args) {
  if (!HANDLERS[type]) {
    console.warn(`sync-queue: onbekende op-type "${type}"`);
    return;
  }
  queue.push({
    type, args,
    id: cryptoRandomId(),
    ts: Date.now()
  });
  saveQueue();
  notify();
  flush();
}

/** Hoofd-entry: probeer direct, queue bij netwerk-fout. */
export function sendOrQueue(type, ...args) {
  const handler = HANDLERS[type];
  if (!handler) {
    console.warn(`sync-queue: onbekende op-type "${type}"`);
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    enqueue(type, ...args);
    return;
  }
  handler(...args).catch(err => {
    if (isOfflineError(err)) {
      enqueue(type, ...args);
    } else {
      console.warn(`Sync ${type} failed:`, err);
    }
  });
}

/** Speel de queue af, FIFO. Stopt bij eerste echte fout zodat de
 *  volgorde behouden blijft — wordt opnieuw geprobeerd bij volgende
 *  enqueue of online-event. */
export async function flush() {
  if (flushing) return;
  if (queue.length === 0) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  flushing = true;
  try {
    while (queue.length > 0) {
      const op = queue[0];
      const handler = HANDLERS[op.type];
      if (!handler) {
        // Onbekende op — dropt 'm zodat we niet vastzitten.
        queue.shift();
        saveQueue();
        notify();
        continue;
      }
      try {
        await handler(...op.args);
        queue.shift();
        saveQueue();
        notify();
      } catch (err) {
        if (isDroppableError(err)) {
          // Conflict-resolutie pijler 3: rij is intussen weg → silent drop.
          console.info(`sync-queue: dropping ${op.type} (resource gone)`);
          queue.shift();
          saveQueue();
          notify();
          continue;
        }
        // Echte fout (netwerk, server) → break en retry later.
        console.warn(`sync-queue: flush onderbroken bij ${op.type}:`, err);
        return;
      }
    }
  } finally {
    flushing = false;
  }
}

function isOfflineError(err) {
  if (!err) return false;
  const msg = String(err.message || '').toLowerCase();
  return err.name === 'TypeError' ||
    /failed to fetch|network|offline|aborted/i.test(msg);
}

function isDroppableError(err) {
  if (!err) return false;
  const code = err?.code || err?.status;
  if (code === 'PGRST116' || code === 404) return true;
  const msg = String(err.message || '').toLowerCase();
  return /not found|does not exist|niet gevonden/.test(msg);
}

function cryptoRandomId() {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

// Auto-flush bij online-komen.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flush(); });
  // Ook bij visibilitychange (tab terug actief) een poging wagen, soms
  // mist 'online' event bij overgang slaap→wakker.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') flush();
  });
  // Boot-time: als er nog items uit een vorige sessie wachten, probeer
  // ze meteen te flushen. Klein delay zodat de Supabase-client klaar is.
  if (queue.length > 0) {
    setTimeout(() => flush(), 200);
  }
}
